import { useState } from 'react';
import { Empty, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { useDeviceCommands } from '@/hooks/useDevices';
import type { CommandDto } from '@/types/command';
import {
  CommandStatusColors,
  CommandStatusLabels,
  CommandStatusMap,
  CommandTypeLabels,
  CommandTypeMap,
} from '@/types/command';

const { Text } = Typography;

interface Props {
  deviceId: number;
}

export function DeviceCommandsTab({ deviceId }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data, isLoading } = useDeviceCommands(deviceId, page, pageSize);

  const columns: TableProps<CommandDto>['columns'] = [
    {
      title: 'Tipo',
      dataIndex: 'commandType',
      key: 'commandType',
      width: 160,
      render: (v: number) => {
        const t = CommandTypeMap[v];
        return <Tag>{CommandTypeLabels[t]}</Tag>;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v: number) => {
        const s = CommandStatusMap[v];
        return <Tag color={CommandStatusColors[s]}>{CommandStatusLabels[s]}</Tag>;
      },
    },
    {
      title: 'Solicitado por',
      dataIndex: 'requestedByName',
      key: 'requestedBy',
      width: 180,
    },
    {
      title: 'Solicitado',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 180,
      render: (v) => (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm:ss')}>
          {dayjs(v).fromNow()}
        </Tooltip>
      ),
    },
    {
      title: 'Completado',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 180,
      render: (v) => v
        ? <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm:ss')}>{dayjs(v).fromNow()}</Tooltip>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Resultado',
      key: 'result',
      render: (_, c) => {
        if (c.errorMessage) {
          return (
            <Text type="danger" style={{ fontSize: 12 }} ellipsis={{ tooltip: c.errorMessage }}>
              {c.errorMessage}
            </Text>
          );
        }
        if (c.responseMessage) {
          return (
            <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: c.responseMessage }}>
              {c.responseMessage}
            </Text>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  if (!isLoading && (data?.totalCount ?? 0) === 0) {
    return <Empty description="Aun no se enviaron comandos a este dispositivo." />;
  }

  return (
    <Table<CommandDto>
      rowKey="id"
      columns={columns}
      dataSource={data?.items ?? []}
      loading={isLoading}
      pagination={{
        current: page,
        pageSize,
        total: data?.totalCount ?? 0,
        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (total) => `${total} en total`,
      }}
      size="small"
    />
  );
}
